'use client';

import { useState, useEffect } from 'react';
import { userBookingService, UserBooking } from '@/lib/services/userBookingService';
import { userRatingService } from '@/lib/services/userRatingService';

interface BookingHistoryProps {
  profileId: string;
}

export default function BookingHistory({ profileId }: BookingHistoryProps) {
  const [bookings, setBookings] = useState<UserBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState<UserBooking | null>(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingData, setRatingData] = useState({
    rating: 5,
    reviewTitle: '',
    reviewText: '',
    professionalismRating: 5,
    punctualityRating: 5,
    careRating: 5,
    communicationRating: 5,
  });

  useEffect(() => {
    loadBookings();
  }, [profileId]);

  const loadBookings = async () => {
    try {
      const data = await userBookingService.getBookingsByProfileId(profileId);
      setBookings(data);
    } catch (error: any) {
      console.error('Failed to load bookings:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRateDriver = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBooking || !selectedBooking.driverId) return;

    try {
      await userRatingService.createRating({
        userProfileId: profileId,
        driverId: selectedBooking.driverId,
        bookingId: selectedBooking.id!,
        ...ratingData,
      });

      alert('Rating submitted successfully!');
      setShowRatingModal(false);
      setSelectedBooking(null);
      setRatingData({
        rating: 5,
        reviewTitle: '',
        reviewText: '',
        professionalismRating: 5,
        punctualityRating: 5,
        careRating: 5,
        communicationRating: 5,
      });
    } catch (error: any) {
      alert(`Failed to submit rating: ${error.message}`);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string }> = {
      scheduled: { bg: 'bg-blue-100', text: 'text-blue-800' },
      in_progress: { bg: 'bg-purple-100', text: 'text-purple-800' },
      completed: { bg: 'bg-green-100', text: 'text-green-800' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800' },
      refunded: { bg: 'bg-gray-100', text: 'text-gray-800' },
    };

    const config = statusConfig[status] || statusConfig.scheduled;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getPaymentStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string }> = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      paid: { bg: 'bg-green-100', text: 'text-green-800' },
      refunded: { bg: 'bg-gray-100', text: 'text-gray-800' },
      failed: { bg: 'bg-red-100', text: 'text-red-800' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-brand-purple border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-card p-6">
        <h2 className="text-xl font-bold text-dark-navy mb-4">Booking History</h2>
        {bookings.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-body-text">No bookings found.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <div key={booking.id} className="border border-gray-200 rounded-lg p-4 hover:border-brand-purple transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold text-dark-navy">Booking #{booking.bookingNumber}</h3>
                      {getStatusBadge(booking.status)}
                      {getPaymentStatusBadge(booking.paymentStatus || 'pending')}
                    </div>
                    <p className="text-sm text-body-text">{booking.serviceType.replace('_', ' ')}</p>
                  </div>
                  {booking.status === 'completed' && booking.driverId && (
                    <button
                      onClick={() => {
                        setSelectedBooking(booking);
                        setShowRatingModal(true);
                      }}
                      className="px-4 py-2 bg-brand-purple text-white text-sm rounded-lg hover:bg-opacity-90 transition-colors"
                    >
                      Rate Driver
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-body-text">Booking Date</p>
                    <p className="text-dark-navy font-medium">{new Date(booking.bookingDate).toLocaleDateString()}</p>
                  </div>
                  {booking.completionDate && (
                    <div>
                      <p className="text-body-text">Completion Date</p>
                      <p className="text-dark-navy font-medium">{new Date(booking.completionDate).toLocaleDateString()}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-body-text">Total Amount</p>
                    <p className="text-dark-navy font-medium">${booking.totalAmount.toFixed(2)}</p>
                  </div>
                  {booking.driverName && (
                    <div>
                      <p className="text-body-text">Driver</p>
                      <p className="text-dark-navy font-medium">{booking.driverName}</p>
                    </div>
                  )}
                  {booking.paymentMethod && (
                    <div>
                      <p className="text-body-text">Payment Method</p>
                      <p className="text-dark-navy font-medium capitalize">{booking.paymentMethod.replace('_', ' ')}</p>
                    </div>
                  )}
                </div>

                {booking.userNotes && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-body-text mb-1">Notes</p>
                    <p className="text-sm text-dark-navy">{booking.userNotes}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rating Modal */}
      {showRatingModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-dark-navy">Rate Your Driver</h3>
              <button
                onClick={() => {
                  setShowRatingModal(false);
                  setSelectedBooking(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleRateDriver} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-dark-navy mb-2">Overall Rating</label>
                <div className="flex space-x-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRatingData({ ...ratingData, rating: star })}
                      className="text-3xl"
                    >
                      {star <= ratingData.rating ? '★' : '☆'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { key: 'professionalismRating', label: 'Professionalism' },
                  { key: 'punctualityRating', label: 'Punctuality' },
                  { key: 'careRating', label: 'Care of Items' },
                  { key: 'communicationRating', label: 'Communication' },
                ].map((category) => (
                  <div key={category.key}>
                    <label className="block text-sm font-medium text-dark-navy mb-2">{category.label}</label>
                    <div className="flex space-x-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRatingData({ ...ratingData, [category.key]: star })}
                          className="text-xl"
                        >
                          {star <= (ratingData as any)[category.key] ? '★' : '☆'}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-navy mb-2">Review Title</label>
                <input
                  type="text"
                  value={ratingData.reviewTitle}
                  onChange={(e) => setRatingData({ ...ratingData, reviewTitle: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent"
                  placeholder="Summarize your experience"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-navy mb-2">Review</label>
                <textarea
                  value={ratingData.reviewText}
                  onChange={(e) => setRatingData({ ...ratingData, reviewText: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent"
                  placeholder="Share your experience with this driver"
                />
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-brand-purple text-white rounded-lg hover:bg-opacity-90 transition-colors"
                >
                  Submit Rating
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRatingModal(false);
                    setSelectedBooking(null);
                  }}
                  className="px-6 py-3 bg-gray-200 text-dark-navy rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}